import { Injectable } from '@angular/core';
import { ToastController } from '@ionic/angular';
import { ApiService } from '../../api/api.service';
import { endpoints } from '../../config/endpoints';
import { DateHelperService } from '../../helpers/date-helper/date-helper.service';
import { FormHelperService } from '../../helpers/form-helper/form-helper.service';
import { AppointmentInventory } from '../../models/appointment-inventory.model';
import { InventoryItem } from '../../models/inventory.model';
import { AppointmentTicketProvision, TicketProvision } from '../../models/ticket-provision.model';
import { LocalStorageService } from '../../storage/local-storage/local-storage.service';

@Injectable({
	providedIn: 'root'
})
export class TicketProvisioningService {
	constructor(
		private storage: LocalStorageService,
		private apiService: ApiService,
		private dateHelper: DateHelperService,
		private formHelper: FormHelperService,
		private toastController: ToastController,
	) { }

	async provisionChanges(apptId: number): Promise<any> {
		const url = endpoints.appointment.inventory.provision
			.replace(':id', `${apptId}`);

		const apptTicketProvisions = await this.storage.pendingTicketProvisions.getItem<AppointmentTicketProvision>(apptId);
		if (!apptTicketProvisions) {
			await this.formHelper.alertUser({ header: 'No Provisions', message: 'No changes were found for provisioning.' });
			return;
		}

		this.apiService.put(url, apptTicketProvisions);
		this.toastProvisionSuccess();
		await this.clearAppointmentProvisions(apptId);
	}

	async clearAppointmentProvisions(apptId: number) {
		await this.storage.pendingTicketProvisions.removeItem(apptId?.toString());
	}

	async addProvision(apptId: number, inventoryItem: AppointmentInventory | InventoryItem) {
		const ticketProvision = this.createNewTicketProvision(inventoryItem);

		let apptTicketProvision = await this.storage.pendingTicketProvisions.getItem<AppointmentTicketProvision>(apptId);
		if (!apptTicketProvision) {
			apptTicketProvision = {
				apptId,
				ticketProvisions: [ticketProvision]
			}
		} else {
			const index = apptTicketProvision.ticketProvisions.findIndex(t => t.inventoryId == ticketProvision.inventoryId &&
				t.equipmentId == ticketProvision.equipmentId && t.primaryAttributeValue == ticketProvision.primaryAttributeValue);
			if (index === -1) {
				apptTicketProvision.ticketProvisions.push(ticketProvision);
			} else {
				apptTicketProvision.ticketProvisions[index] = ticketProvision;
			}
		}

		await this.storage.pendingTicketProvisions.setItem(apptId, apptTicketProvision);
	}

	private createNewTicketProvision(inventoryItem: AppointmentInventory | InventoryItem) {
		return {
			inventoryId: inventoryItem.id,
			equipmentId: inventoryItem.equipId,
			primaryAttributeValue: inventoryItem.attributes[0].value,
			timestamp: this.dateHelper.getNowISO()
		} as TicketProvision;
	}

	private async toastProvisionSuccess(): Promise<void> {
		const toast = await this.toastController.create({
			message: 'Successfully sent provisions to server.',
			duration: 5000,
			position: 'top',
			color: 'success',
			buttons: ['Dismiss'],
		});

		await toast.present();
	}
}
